import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { songWrapSlug, artistHasRecap } from "@/lib/wrapLinks";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Plus, Disc3, ChevronRight, Link2, Share2 } from "lucide-react";
import api, { thumbUrl } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { PlatformBadge } from "@/components/PlatformBadge";
import { TrackSoundDialog } from "@/components/dialogs/TrackSoundDialog";
import { ImportLinkDialog } from "@/components/dialogs/ImportLinkDialog";
import { ShareDialog } from "@/components/dialogs/ShareDialog";
import { PressSection } from "@/components/PressSection";
import { MetaAdsSection } from "@/components/MetaAdsSection";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtFull } from "@/lib/format";

const statusPill = (s) =>
  s === "complete"
    ? "bg-[#D1FAE5] text-[#065F46]"
    : "bg-[#FEF3C7] text-[#92400E]";

export default function ArtistDetail() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [track, setTrack] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = () => api.get(`/artists/${artistId}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load artist"));
  useEffect(() => { load(); }, [artistId]);

  const deletePress = async (p) => {
    try {
      await api.delete(`/press/${p.id}`);
      toast.success("Press removed");
      load();
    } catch { toast.error("Couldn't remove press"); }
  };

  const shareUrl = `${window.location.origin}/share/artist/${artistId}`;

  const artist = data?.artist;
  const songs = data?.songs || [];

  return (
    <div className="min-h-screen">
      <TopBar
        right={
          <div className="flex items-center gap-2">
            {artistHasRecap(artist?.name) && <Link data-testid="artist-wrap-recap-link" to="/wraps#recap" className="mr-1 text-sm font-medium text-[#35606E] hover:underline">Wrap recap</Link>}
            <Button data-testid="artist-share-btn" onClick={() => setShareOpen(true)} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
              <Share2 className="mr-1.5 h-4 w-4" /> Share
            </Button>
            <Button data-testid="track-sound-btn" onClick={() => setTrack(true)} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
              <Plus className="mr-1.5 h-4 w-4" /> Track a Sound
            </Button>
          </div>
        }
      />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <button data-testid="back-to-artists" onClick={() => navigate("/")} className="mb-6 flex items-center gap-1.5 text-sm text-neutral-500 transition-colors hover:text-black">
          <ArrowLeft className="h-4 w-4" /> Artists
        </button>

        {!data ? (
          <Skeleton className="h-40 rounded-2xl" />
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="pb-card flex flex-col gap-5 p-6 sm:flex-row sm:items-center">
            <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl bg-neutral-900 shadow-lg ring-1 ring-black/5 sm:h-36 sm:w-36">
              {artist.image ? <img src={thumbUrl(artist.image, 240)} alt={artist.name} width={96} height={96} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-8 w-8" /></div>}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-neutral-500">Artist</p>
              <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{artist.name}</h1>
              <p className="mt-1 text-neutral-500">{songs.length} song{songs.length === 1 ? "" : "s"} tracked</p>
              {{"voilà": "voila", "voila": "voila", "akeem ali": "akeem-ali"}[(artist.name || "").trim().toLowerCase()] && (
                <a
                  data-testid="tour-leaderboard-link"
                  href={`/tour/${{"voilà": "voila", "voila": "voila", "akeem ali": "akeem-ali"}[(artist.name || "").trim().toLowerCase()]}`}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#35606E] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#294b57]"
                >
                  View Tour Leaderboard
                </a>
              )}
            </div>
            <Button
              data-testid="add-dashboard-link-btn"
              onClick={() => setImportOpen(true)}
              variant="outline"
              className="shrink-0 self-start rounded-full border-[#35606E]/30 bg-white text-[#35606E] hover:bg-[#35606E]/5 sm:self-center"
            >
              <Link2 className="mr-1.5 h-4 w-4" /> Add dashboard link
            </Button>
          </motion.div>
        )}

        <h2 className="mb-4 mt-10 font-display text-xl font-bold">Songs</h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {data &&
            songs.map((s, i) => (
              <motion.button
                key={s.id}
                data-testid={`song-card-${s.id}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                whileHover={{ y: -5 }}
                onClick={() => navigate(`/songs/${s.id}`)}
                className="pb-card group flex flex-col overflow-hidden p-0 text-left"
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-neutral-100">
                  {(s.cover_art || artist?.image) ? (
                    <img src={thumbUrl(s.cover_art || artist?.image, 160)} alt={s.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="grid h-full place-items-center bg-neutral-900 text-neutral-600"><Disc3 className="h-10 w-10" /></div>
                  )}
                  <div className="absolute right-3 top-3 flex gap-1.5">{s.platforms.map((p) => <PlatformBadge key={p} platform={p} />)}</div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-lg font-bold leading-tight">{s.title}</h3>
                  <p className="text-sm text-neutral-500">{s.campaign_count} campaign{s.campaign_count === 1 ? "" : "s"}</p>
                  {songWrapSlug(artist?.name, s.title) && <span role="link" tabIndex={0} data-testid={`song-card-wrap-link-${s.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/wraps/${songWrapSlug(artist?.name, s.title)}`); }} className="mt-1 w-fit text-xs font-medium text-[#35606E] hover:underline">Wrap report</span>}
                  <div className="mt-4 flex items-end justify-between border-t border-black/[0.06] pt-4">
                    <div>
                      <p className="num-display text-2xl font-extrabold">{s.summary.total_views == null ? "—" : fmtFull(s.summary.total_views)}</p>
                      <p className="text-xs text-neutral-500">{s.summary.post_count} posts · {s.summary.avg_engagement_rate == null ? "—" : `${s.summary.avg_engagement_rate}%`} avg ER</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-neutral-300 transition-transform group-hover:translate-x-1 group-hover:text-neutral-900" />
                  </div>
                </div>
              </motion.button>
            ))}

          {data && (
            <button
              data-testid="add-campaign-card"
              onClick={() => setTrack(true)}
              className="group flex min-h-[16rem] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-neutral-300 text-neutral-400 transition-colors hover:border-neutral-900 hover:text-neutral-900"
            >
              <span className="grid h-12 w-12 place-items-center rounded-full bg-neutral-100 transition-colors group-hover:bg-neutral-900 group-hover:text-white">
                <Plus className="h-5 w-5" />
              </span>
              <span className="font-medium">Track a new sound</span>
            </button>
          )}
        </div>

        {data && (
          <MetaAdsSection className="mt-12" artistId={artistId} songs={songs} />
        )}

        {data && (
          <PressSection className="mt-12" items={data.press || []} onDelete={deletePress} />
        )}
      </main>

      <TrackSoundDialog
        open={track}
        onOpenChange={setTrack}
        artistId={artist?.id}
        artistName={artist?.name}
        onCreated={(c) => navigate(`/songs/${c.song_id}`)}
      />
      <ImportLinkDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        artistId={artist?.id}
        onImported={(res) => navigate(`/songs/${res.song_id}`)}
      />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} url={shareUrl} type="artist" id={artistId} />
    </div>
  );
}
